import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Chip,
  Badge,
  CircularProgress,
  Alert
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  Close as CloseIcon,
  Person as PersonIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { format, isToday, isYesterday } from 'date-fns';
import { fr } from 'date-fns/locale';
import { useTranslation } from 'react-i18next';
import socketService from '../services/socketService';
import conversationApi from '../services/conversationApi';

const MessagingContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'transparent',
  borderRadius: 0,
  overflow: 'hidden',
  boxShadow: 'none'
}));

const MessagesContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  padding: theme.spacing(1),
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  '&::-webkit-scrollbar': {
    width: '6px',
  },
  '&::-webkit-scrollbar-track': {
    background: 'transparent',
  },
  '&::-webkit-scrollbar-thumb': {
    background: theme.palette.divider,
    borderRadius: '3px',
  }
}));

const MessageBubble = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isOwn' && prop !== 'messageType'
})(({ theme, isOwn, messageType }) => ({
  display: 'flex',
  justifyContent: isOwn ? 'flex-end' : 'flex-start',
  marginBottom: theme.spacing(1),
  '& .message-content': {
    maxWidth: '70%',
    padding: theme.spacing(1, 1.5),
    borderRadius: theme.spacing(2),
    backgroundColor: isOwn ? theme.palette.primary.main : theme.palette.grey[100],
    color: isOwn ? theme.palette.primary.contrastText : theme.palette.text.primary,
    position: 'relative',
    wordBreak: 'break-word',
    ...(messageType === 'system' && {
      backgroundColor: theme.palette.info.light,
      color: theme.palette.info.contrastText,
      textAlign: 'center',
      fontStyle: 'italic',
      maxWidth: '90%',
      margin: '0 auto'
    })
  }
}));

const InputContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  backgroundColor: theme.palette.background.paper,
  borderTop: `1px solid ${theme.palette.divider}`,
  display: 'flex',
  alignItems: 'flex-end',
  gap: theme.spacing(1)
}));

const formatMessageTime = (timestamp, t) => {
  const date = new Date(timestamp);
  
  if (isToday(date)) {
    return format(date, 'HH:mm', { locale: fr });
  } else if (isYesterday(date)) {
    return `${t('messaging.yesterday')} ${format(date, 'HH:mm', { locale: fr })}`;
  } else {
    return format(date, 'dd/MM HH:mm', { locale: fr });
  }
};

const MessagingInterface = ({ 
  reservationId, 
  onClose, 
  currentUser,
  reservation,
  embedded = false, // Nouveau prop pour mode intégré
  unreadCount = 0 // Nouveau prop pour le nombre de messages non lus
}) => {
  const { t } = useTranslation('messaging');
  const { t: tCommon } = useTranslation('common');
  const [messages, setMessages] = useState([]);
  const [conversation, setConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [otherParticipant, setOtherParticipant] = useState(null);
  const messagesEndRef = useRef(null);
  const textFieldRef = useRef(null);

  // Déterminer l'autre participant dans la conversation
  const getOtherParticipant = (conversationData) => {
    if (!conversationData?.reservation) return null;
    
    const { renter, owner } = conversationData.reservation;
    const isCurrentUserRenter = currentUser.id === renter?.id;
    
    return isCurrentUserRenter ? owner : renter;
  };

  // Faire défiler vers le bas automatiquement
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Charger la conversation
  useEffect(() => {
    loadConversation();
  }, [reservationId]);

  // Configuration des événements socket
  useEffect(() => {
    if (!conversation) return;

    const handleNewMessage = (data) => {
      if (data.conversation_id === conversation.id) {
        setMessages(prev => [...prev, data.message]);
        // Marquer comme lu automatiquement si la fenêtre est active
        if (document.hasFocus()) {
          markMessageAsRead(data.message.id);
        }
      }
    };

    const handleMessageRead = (data) => {
      if (data.conversation_id === conversation.id) {
        setMessages(prev => prev.map(msg => 
          msg.id === data.message_id 
            ? { ...msg, read_by: [...(msg.read_by || []), data.read_by] }
            : msg
        ));
      }
    };

    socketService.on('new_message', handleNewMessage);
    socketService.on('message_read', handleMessageRead);

    return () => {
      socketService.off('new_message');
      socketService.off('message_read');
    };
  }, [conversation]);

  const loadConversation = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await conversationApi.getConversation(reservationId);
      if (response.success) {
        const conversationData = response.data.conversation;
        setConversation(conversationData);
        setMessages(response.data.messages);
        setOtherParticipant(getOtherParticipant(response.data));
        
        // Marquer tous les messages comme lus avec l'ID de la conversation qui vient d'être chargée
        try {
          console.log('Marking messages as read for conversation:', conversationData.id);
          await conversationApi.markAllAsRead(conversationData.id);
        } catch (markReadError) {
          console.error('Error marking messages as read:', markReadError);
          // Afficher l'erreur dans la console mais ne pas interrompre l'expérience utilisateur
          if (markReadError.response?.status === 403) {
            console.warn('Access denied when marking messages as read. The conversation may have been accessed incorrectly.');
          }
          // Ne pas faire échouer le chargement de la conversation à cause de cette erreur
        }
      }
    } catch (err) {
      console.error('Error loading conversation:', err);
      setError(t('errors.loadConversation'));
    } finally {
      setLoading(false);
    }
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || sending || !conversation) return;

    try {
      setSending(true);
      
      const response = await conversationApi.sendMessage(conversation.id, {
        message: newMessage.trim(),
        message_type: 'text'
      });

      if (response.success) {
        setMessages(prev => [...prev, response.data]);
        setNewMessage('');
        textFieldRef.current?.focus();
      }
    } catch (err) {
      console.error('Error sending message:', err);
      setError(t('errors.sendMessage'));
    } finally {
      setSending(false);
    }
  };

  const markMessageAsRead = async (messageId) => {
    if (!conversation) return;
    
    try {
      await conversationApi.markAsRead(conversation.id, messageId);
    } catch (err) {
      console.error('Error marking message as read:', err);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };


  if (loading) {
    return (
      <MessagingContainer>
        <Box display="flex" justifyContent="center" alignItems="center" height="100%">
          <CircularProgress />
        </Box>
      </MessagingContainer>
    );
  }

  if (error) {
    return (
      <MessagingContainer>
        <Box p={2}>
          <Alert severity="error" action={
            <IconButton size="small" onClick={onClose}>
              <CloseIcon />
            </IconButton>
          }>
            {error}
          </Alert>
        </Box>
      </MessagingContainer>
    );
  }

  return (
    <MessagingContainer>
      {/* En-tête - version complète ou simplifiée selon le mode */}
      {!embedded ? (
        <Box
          sx={{
            p: 2,
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box display="flex" alignItems="center" gap={2}>
            <Badge
              overlap="circular"
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              badgeContent={
                <Box
                  sx={{
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    backgroundColor: 'success.main',
                    border: '2px solid white'
                  }}
                />
              }
            >
              <Avatar
                src={otherParticipant?.profilePicture}
                sx={{ width: 40, height: 40 }}
              >
                {otherParticipant?.name?.[0] || <PersonIcon />}
              </Avatar>
            </Badge>
            <Box>
              <Typography variant="h6" component="h2">
                {otherParticipant?.name || tCommon('profile.user')}
              </Typography>
              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                {tCommon('profile.reservation')} #{reservationId.slice(-8).toUpperCase()}
              </Typography>
            </Box>
          </Box>
          <IconButton 
            onClick={onClose} 
            sx={{ color: 'primary.contrastText' }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      ) : (
        /* En-tête simplifié pour mode embedded */
        <Box
          sx={{
            p: 2,
            background: 'linear-gradient(135deg, #133cf3 0%, #5d06b4 100%)',
            borderBottom: '1px solid rgba(0,0,0,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: 2
          }}
        >
          <Badge
            badgeContent={unreadCount > 0 ? unreadCount : null}
            color="error"
            overlap="circular"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '0.75rem',
                height: '18px',
                minWidth: '18px',
                fontWeight: 600
              }
            }}
          >
            <Avatar
              src={otherParticipant?.profilePicture}
              sx={{ width: 36, height: 36 }}
            >
              {otherParticipant?.name?.[0] || <PersonIcon />}
            </Avatar>
          </Badge>
          <Box sx={{ flex: 1 }}>
            <Typography variant="body1" fontWeight="600" color="white">
              {otherParticipant?.name || tCommon('profile.user')}
              {unreadCount > 0 && (
                <Chip
                  label={t('unreadMessages', { count: unreadCount })}
                  size="small"
                  color="error"
                  variant="filled"
                  sx={{ 
                    ml: 1,
                    height: '20px',
                    fontSize: '0.7rem',
                    fontWeight: 600
                  }}
                />
              )}
            </Typography>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
              {tCommon('profile.reservation')} #{reservationId.slice(-8).toUpperCase()}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Messages */}
      <MessagesContainer>
        {messages.length === 0 ? (
          <Box 
            display="flex" 
            flexDirection="column" 
            alignItems="center" 
            justifyContent="center" 
            height="100%"
            color="text.secondary"
          >
            <Typography variant="h6" gutterBottom>
              {t('startConversation.title')}
            </Typography>
            <Typography variant="body2" textAlign="center">
              {t('startConversation.subtitle', { name: otherParticipant?.name || tCommon('profile.thisUser') })}
            </Typography>
          </Box>
        ) : (
          messages.map((message, index) => {
            const isOwn = message.sender_id === currentUser.id;
            const showAvatar = index === 0 || messages[index - 1].sender_id !== message.sender_id;
            
            return (
              <MessageBubble 
                key={message.id} 
                isOwn={isOwn}
                messageType={message.message_type}
              >
                {!isOwn && showAvatar && (
                  <Avatar
                    src={message.sender?.profilePicture || otherParticipant?.profilePicture}
                    sx={{ width: 32, height: 32, mr: 1, alignSelf: 'flex-end' }}
                  >
                    {(message.sender?.name || otherParticipant?.name)?.[0] || <PersonIcon />}
                  </Avatar>
                )}
                <Box className="message-content">
                  {!isOwn && showAvatar && (
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        display: 'block', 
                        mb: 0.5, 
                        fontWeight: 600,
                        color: 'primary.main'
                      }}
                    >
                      {message.sender?.name || otherParticipant?.name || tCommon('profile.user')}
                    </Typography>
                  )}
                  {/* Message formaté avec sauts de ligne préservés */}
                  <Typography 
                    variant="body1"
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      lineHeight: 1.5
                    }}
                  >
                    {message.message}
                  </Typography>
                  
                  {/* Affichage des photos attachées - photo unique */}
                  {message.attachment_url && message.attachment_type === 'image' && (
                    <Box sx={{ mt: 1 }}>
                      <img
                        src={message.attachment_url}
                        alt={message.attachment_name || 'Photo attachée'}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '200px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                        onError={(e) => {
                          console.error('Erreur chargement image:', message.attachment_url);
                          e.target.style.display = 'none';
                        }}
                        onClick={() => {
                          // Ouvrir l'image en grand dans un nouvel onglet
                          window.open(message.attachment_url, '_blank');
                        }}
                      />
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          mt: 0.5, 
                          fontSize: '0.7rem',
                          opacity: 0.8,
                          fontStyle: 'italic'
                        }}
                      >
                        {message.attachment_name || 'Photo attachée'}
                      </Typography>
                    </Box>
                  )}

                  {/* Affichage des photos multiples depuis system_data */}
                  {message.system_data && Array.isArray(message.system_data) && message.system_data.length > 0 && (
                    <Box sx={{ mt: 1 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          display: 'block', 
                          mb: 1, 
                          fontWeight: 600,
                          color: 'primary.main'
                        }}
                      >
                        Photos du rapport :
                      </Typography>
                      <Box sx={{ 
                        display: 'flex', 
                        flexWrap: 'wrap', 
                        gap: 1,
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        {message.system_data.map((photoUrl, index) => (
                          <Box key={index} sx={{ position: 'relative' }}>
                            <img
                              src={photoUrl}
                              alt={`Photo ${index + 1}`}
                              style={{
                                width: '80px',
                                height: '80px',
                                borderRadius: '6px',
                                objectFit: 'cover',
                                cursor: 'pointer',
                                border: '1px solid #e0e0e0'
                              }}
                              onError={(e) => {
                                console.error('Erreur chargement photo:', photoUrl);
                                e.target.style.display = 'none';
                              }}
                              onClick={() => {
                                // Ouvrir l'image en grand dans un nouvel onglet
                                window.open(photoUrl, '_blank');
                              }}
                            />
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                position: 'absolute',
                                bottom: 0,
                                left: 0,
                                right: 0,
                                backgroundColor: 'rgba(0,0,0,0.7)',
                                color: 'white',
                                textAlign: 'center',
                                fontSize: '0.6rem',
                                borderBottomLeftRadius: '6px',
                                borderBottomRightRadius: '6px',
                                padding: '2px'
                              }}
                            >
                              {index + 1}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  )}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      display: 'block', 
                      mt: 0.5, 
                      opacity: 0.7,
                      fontSize: '0.75rem'
                    }}
                  >
                    {formatMessageTime(message.created_at, t)}
                    {isOwn && message.read_by?.length > 1 && (
                      <Chip 
                        label={t('messageStatus.read')} 
                        size="small" 
                        sx={{ 
                          ml: 1, 
                          height: 16, 
                          fontSize: '0.6rem',
                          backgroundColor: 'success.light',
                          color: 'success.contrastText'
                        }} 
                      />
                    )}
                  </Typography>
                </Box>
                {isOwn && showAvatar && (
                  <Avatar
                    src={currentUser.profilePicture}
                    sx={{ width: 32, height: 32, ml: 1, alignSelf: 'flex-end' }}
                  >
                    {currentUser.name?.[0] || <PersonIcon />}
                  </Avatar>
                )}
              </MessageBubble>
            );
          })
        )}
        <div ref={messagesEndRef} />
      </MessagesContainer>

      {/* Zone de saisie */}
      <InputContainer>
        <TextField
          ref={textFieldRef}
          fullWidth
          multiline
          maxRows={3}
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={t('input.placeholder')}
          disabled={sending}
          variant="outlined"
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 3,
              backgroundColor: 'background.default'
            }
          }}
        />
        <IconButton 
          color="primary" 
          onClick={sendMessage}
          disabled={!newMessage.trim() || sending}
          sx={{ 
            backgroundColor: 'primary.main',
            color: 'primary.contrastText',
            '&:hover': {
              backgroundColor: 'primary.dark'
            },
            '&:disabled': {
              backgroundColor: 'action.disabled'
            }
          }}
        >
          {sending ? <CircularProgress size={20} /> : <SendIcon />}
        </IconButton>
      </InputContainer>
    </MessagingContainer>
  );
};

export default MessagingInterface;
